import { ManualAdjustmentReasonsQuery, ManualAdjustmentReasonType } from '../../../../../graphql'
import { AdjustQuantityFormState } from '../index'
import { compareCost, CostComparison } from 'views/helpers/utils'

// Extract the actual ManualAdjustmentReason type returned by the query
type ManualAdjustmentReasonNode = ManualAdjustmentReasonsQuery['manualAdjustmentReasons'][number]

export type SetUnexpectedCost = ( flag: CostComparison ) => void

export function checkForUnexpectedCost( formState: AdjustQuantityFormState, itemCost: number | undefined, setUnexpectedCost: SetUnexpectedCost ) {
	const floatCost = parseFloat( formState.cost )
	let validation: { isInvalid: boolean, error?: string } = { isInvalid: false }
	let comparison: CostComparison = 'normal'

	if ( Number.isFinite( floatCost ) ) {
		comparison = compareCost( floatCost, itemCost )

		if ( comparison === 'high' ) {
			validation = {
				isInvalid: true,
				error: 'High price',
			}
		} else if ( comparison === 'low' ) {
			validation = {
				isInvalid: true,
				error: 'Low price',
			}
		}
	}

	setUnexpectedCost( comparison )
	return validation
}

export function validateReasonQuantity( quantity: number, reason: NonNullable<ManualAdjustmentReasonNode>, availableQuantity: number ) {
	let quantityValidation: { isInvalid: boolean, error?: string } = { isInvalid: false }
	let isInvalid = false

	if ( quantity < availableQuantity && !reason.allowDecrement ) {
		quantityValidation = { isInvalid: true, error: 'Quantity must be increased for selected reason' }
		isInvalid = true
	}
	if ( quantity > availableQuantity && !reason.allowIncrement ) {
		quantityValidation = { isInvalid: true, error: 'Quantity must be decreased for selected reason' }
		isInvalid = true
	}

	return {
		isInvalid,
		quantityValidation,
	}
}

export default function validateForm( formState: AdjustQuantityFormState, availableQuantity: number ) {
	const { cost, quantity, reason } = formState
	const newFormState = { ...formState }
	let isInvalid = false

	const numericQuantity = parseInt( quantity, 10 )

	if ( quantity === '' || isNaN( numericQuantity ) ) {
		newFormState.validation.quantity = { isInvalid: true, error: 'Quantity is required' }
		isInvalid = true
	} else if ( numericQuantity === availableQuantity ) {
		newFormState.validation.quantity = { isInvalid: true, error: 'Quantity must not equal on hand quantity' }
		isInvalid = true
	}

	if ( cost === '' || isNaN( parseFloat( cost ) ) ) {
		newFormState.validation.cost = { isInvalid: true, error: 'Cost is required' }
		isInvalid = true
	}

	if ( parseFloat( cost ) <= 0 ) {
		newFormState.validation.cost = { isInvalid: true, error: 'Cost must be more than $0' }
		isInvalid = true
	}

	if ( !reason ) {
		newFormState.validation.reason = { isInvalid: true, error: 'Reason is required' }
		isInvalid = true
	} else if ( !newFormState.validation.quantity.isInvalid ) {
		const { isInvalid: quantityReasonInvalid, quantityValidation } = validateReasonQuantity( numericQuantity, reason, availableQuantity )
		newFormState.validation.quantity = quantityValidation
		isInvalid = isInvalid || quantityReasonInvalid
	}

	// Vendor validation for "Return to Vendor" reason
	const isReturnToVendor = reason?.reason === ManualAdjustmentReasonType.ReturnToVendor
	if ( isReturnToVendor ) {
		// Must select an acquisition method
		if ( !formState.acquisitionMethod ) {
			newFormState.validation.vendor = { isInvalid: true, error: 'Please select how the product was acquired' }
			newFormState.validation.purchaseOrder = { isInvalid: false }
			isInvalid = true
		} else if ( formState.acquisitionMethod === 'direct_vendor' ) {
			// Direct vendor requires a vendor selection
			if ( !formState.vendorId ) {
				newFormState.validation.vendor = { isInvalid: true, error: 'Vendor is required when returning to vendor' }
				isInvalid = true
			} else {
				newFormState.validation.vendor = { isInvalid: false }
			}
			newFormState.validation.purchaseOrder = { isInvalid: false }
		} else if ( formState.acquisitionMethod === 'purchase_order' ) {
			// Purchase order requires both PO and vendor (vendor comes from PO selection)
			if ( !formState.purchaseOrderId || !formState.vendorId ) {
				newFormState.validation.purchaseOrder = { isInvalid: true, error: 'Purchase order is required when returning via purchase order' }
				isInvalid = true
			} else {
				newFormState.validation.purchaseOrder = { isInvalid: false }
			}
			newFormState.validation.vendor = { isInvalid: false }
		}
	} else {
		// Clear vendor validations when not returning to vendor
		newFormState.validation.vendor = { isInvalid: false }
		newFormState.validation.purchaseOrder = { isInvalid: false }
	}
	return {
		isInvalid,
		newFormState,
	}
}
